/**
 * https://ui.shadcn.com/docs/components/tooltip
 */
"use client";

import { useIsMobile } from "@/hooks/use-mobile";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as React from "react";
import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";

const TooltipProvider = TooltipPrimitive.Provider;

type TooltipProps = React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root>;

const Tooltip = ({ children, ...props }: TooltipProps) => {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    return () => {
      if (isMobile) {
        setOpen(false);
      }
    };
  }, [isMobile]);

  return (
    <TooltipPrimitive.Root
      {...props}
      open={isMobile ? open : props.open}
      onOpenChange={isMobile ? setOpen : props.onOpenChange}
    >
      {children}
    </TooltipPrimitive.Root>
  );
};
Tooltip.displayName = "Tooltip";

type TooltipTriggerProps = React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>;

const TooltipTrigger = React.forwardRef<React.ElementRef<typeof TooltipPrimitive.Trigger>, TooltipTriggerProps>(
  ({ className, onClick, ...props }, ref) => {
    const isMobile = useIsMobile();

    return (
      <TooltipPrimitive.Trigger
        ref={ref}
        className={cn("flex items-center", className)}
        onClick={(e) => {
          if (isMobile) {
            e.preventDefault();
            onClick?.(e);
          }
        }}
        {...props}
      />
    );
  },
);
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName;

type TooltipPosition = "topLeft" | "topMiddle" | "topRight" | "bottomLeft" | "bottomMiddle" | "bottomRight";

interface TooltipContentProps extends React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> {
  position?: TooltipPosition;
}

const TooltipContent = React.forwardRef<React.ElementRef<typeof TooltipPrimitive.Content>, TooltipContentProps>(
  ({ className, sideOffset = 8, position = "bottomMiddle", ...props }, ref) => {
    const isMobile = useIsMobile();

    return (
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          ref={ref}
          sideOffset={sideOffset}
          className={cn(
            "font-inter text-xs font-normal leading-[1.4] text-borderLight text-center",
            "max-w-[280px] p-3 z-50 overflow-visible rounded-[8px] bg-textSecondary",
            "animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            "relative",
            {
              'after:content-[""] after:absolute after:w-4 after:h-2 after:bg-textSecondary': true,
              "after:bottom-[calc(100%-1px)] after:clip-path-triangle-up": position.startsWith("top"),
              "after:top-[calc(100%-1px)] after:clip-path-triangle-down": position.startsWith("bottom"),
              "after:left-3": position === "topLeft" || position === "bottomLeft",
              "after:left-1/2 after:-translate-x-1/2": position === "topMiddle" || position === "bottomMiddle",
              "after:right-3": position === "topRight" || position === "bottomRight",
            },
            isMobile && "after:hidden",
            className,
          )}
          {...props}
        />
      </TooltipPrimitive.Portal>
    );
  },
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
